import { useMemo, useState } from 'react';
import { useRigg } from '@/data/RiggProvider.tsx';
import { KIND_LABELS, type TaskKind } from '@shared/domain.ts';
import { kindOptions } from '@/components/TaskFacts.tsx';
import { api } from '@/lib/api.ts';
import { ago } from '@/lib/format.ts';
import { Page, Card, Row } from './Page.tsx';
import {
  Button,
  Checkbox,
  Dialog,
  Empty,
  Field,
  Input,
  SectionLabel,
  Select,
  Tip,
  useToast,
} from '@/components/ui.tsx';

/**
 * Intakes — named ways work arrives.
 *
 * Opening a task programmatically already worked; what did not was doing it
 * twice the same way. Every caller had to be told the project, the category, the
 * routine and a token, and each one that got it slightly wrong produced tasks
 * that looked almost right. An intake is that decision made once and given a
 * name, so the caller only has to say what happened.
 */
export function IntakesScreen() {
  const { data, project, isAdmin } = useRigg();
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [showing, setShowing] = useState<string | null>(null);

  const intakes = useMemo(
    () => Object.values(data.intakes).sort((a, b) => a.name.localeCompare(b.name)),
    [data.intakes],
  );

  return (
    <Page
      title="Intakes"
      blurb="A named way for something outside Rigg to open a task — a feedback form, an alerting rule, a nightly job. Each one already knows its category and which routine gets the work, so the caller only says what happened."
      actions={
        isAdmin ? (
          <Button tone="primary" onClick={() => setCreating(true)}>
            New intake
          </Button>
        ) : null
      }
    >
      {intakes.length === 0 ? (
        <Empty
          title="No intakes yet"
          hint="Make one for anything that needs to open tasks without a person in the loop."
        />
      ) : (
        <Card>
          {intakes.map((intake) => (
            <Row
              key={intake.id}
              title={intake.name}
              subtitle={
                <span className="text-[12.5px]">
                  {intake.category_id
                    ? (data.categories[intake.category_id]?.label ?? 'a deleted category')
                    : 'no category'}
                  {intake.kind ? ` · ${KIND_LABELS[intake.kind]}` : ''}
                  {' · '}
                  {intake.agent_id
                    ? (data.agents[intake.agent_id]?.name ?? 'a deleted routine')
                    : intake.smart
                      ? 'next smart routine in rotation'
                      : 'next routine in rotation'}
                  {intake.auto_merge ? (
                    <>
                      {' · '}
                      <span className="text-warn">auto-merge</span>
                    </>
                  ) : null}
                  {intake.fire_and_forget ? (
                    <>
                      {' · '}
                      <span className="text-mute">fire and forget</span>
                    </>
                  ) : null}
                  {intake.smart ? (
                    <>
                      {' · '}
                      <span className="text-mute">smart</span>
                    </>
                  ) : null}
                </span>
              }
              meta={
                <span className="text-[11.5px] text-mute">
                  {intake.use_count === 0
                    ? 'never used'
                    : `${intake.use_count} task${intake.use_count === 1 ? '' : 's'}${
                        intake.last_used_at ? ` · ${ago(intake.last_used_at, Date.now())}` : ''
                      }`}
                </span>
              }
              actions={
                <>
                  <Button size="sm" onClick={() => setShowing(intake.id)}>
                    URL and instructions
                  </Button>
                  {isAdmin ? (
                    <Button size="sm" onClick={() => setEditing(intake.id)}>
                      Edit
                    </Button>
                  ) : null}
                </>
              }
            />
          ))}
        </Card>
      )}

      {creating || editing ? (
        <IntakeDialog
          intakeId={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onCreated={(id) => {
            setCreating(false);
            // Straight to the URL: making one and not being shown how to use it
            // is the moment somebody gives up and hardcodes a token elsewhere.
            setShowing(id);
          }}
        />
      ) : null}

      {showing ? <IntakeUrlDialog intakeId={showing} onClose={() => setShowing(null)} /> : null}

      {!project ? null : null}
    </Page>
  );
}

function IntakeDialog({
  intakeId,
  onClose,
  onCreated,
}: {
  intakeId: string | null;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { data, project } = useRigg();
  const { notify } = useToast();
  const existing = intakeId ? data.intakes[intakeId] : null;

  const [name, setName] = useState(existing?.name ?? '');
  const [category, setCategory] = useState(
    existing?.category_id ? (data.categories[existing.category_id]?.label ?? '') : '',
  );
  const [agentId, setAgentId] = useState(existing?.agent_id ?? 'auto');
  const [autoMerge, setAutoMerge] = useState(existing?.auto_merge ?? false);
  const [fireAndForget, setFireAndForget] = useState(existing?.fire_and_forget ?? false);
  const [smart, setSmart] = useState(existing?.smart ?? false);
  const [kind, setKind] = useState<string>(existing?.kind ?? 'none');
  const [busy, setBusy] = useState(false);

  const agents = Object.values(data.agents);

  const save = async () => {
    if (!project) return;
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        category: fireAndForget ? null : category.trim() || null,
        agent_id: agentId === 'auto' ? null : agentId,
        auto_merge: autoMerge,
        fire_and_forget: fireAndForget,
        smart,
        kind: kind === 'none' ? null : (kind as TaskKind),
      };
      if (existing) {
        await api.updateIntake(existing.id, payload);
        onClose();
      } else {
        const { intake } = await api.addIntake({ project_id: project.id, ...payload });
        onCreated(intake.id);
      }
    } catch (error) {
      notify('Could not save the intake', { body: (error as Error).message, tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={existing ? `Edit ${existing.name}` : 'New intake'}
      description="Everything here is decided once, so whoever wires it up only has to send the prompt."
      footer={
        <>
          {existing ? (
            <Button
              tone="danger"
              className="mr-auto"
              disabled={busy}
              onClick={async () => {
                try {
                  await api.removeIntake(existing.id);
                  onClose();
                } catch (error) {
                  notify('Could not delete', { body: (error as Error).message, tone: 'bad' });
                }
              }}
            >
              Delete
            </Button>
          ) : null}
          <Button onClick={onClose}>Cancel</Button>
          <Button tone="primary" disabled={busy || !name.trim()} onClick={() => void save()}>
            {existing ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <Field label="Name" hint="What this is for, as a person would say it. Appears on every task it opens.">
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="User feedback"
          autoFocus
        />
      </Field>
      {fireAndForget ? null : (
        <Field
          label="Category"
          hint="Applied to every task from this intake. Type anything — a category that does not exist yet is created."
        >
          <Input
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            placeholder="Feedback"
          />
        </Field>
      )}
      <Field label="Kind" hint="Pinned on every task from this intake, the same way the category is.">
        <Select value={kind} onChange={setKind} options={kindOptions('No kind')} aria-label="Kind" />
      </Field>
      <Field label="Target routine" hint="Or leave it on rotation and let Rigg pick whoever is next.">
        <Select
          value={agentId}
          onChange={setAgentId}
          options={[
            { value: 'auto', label: 'Auto — next routine in rotation' },
            ...agents.map((agent) => ({ value: agent.id, label: agent.name })),
          ]}
        />
      </Field>
      {agentId === 'auto' ? (
        <div className="flex flex-col gap-1.5">
          <Checkbox
            id="intake-smart"
            checked={smart}
            onChange={setSmart}
            label="Pick a smart routine instead of a normal one"
          />
        </div>
      ) : null}
      {/*
        Deliberately the last field and phrased as a consequence rather than a
        setting. It is the one thing here that removes a person from the loop,
        so the hint says what it waives AND what it does not — a reader who
        takes "no approval" to mean "no gates" would be wrong about the three
        that still hold.

        A <div> rather than <Field>, and that is not cosmetic. `Field` is a
        bare <label> with no `htmlFor`, so its labelled control resolves to the
        first labelable descendant — which for a Checkbox is Radix's
        <button role="checkbox">. Nested inside it, clicking anywhere on the
        heading OR the hint paragraph would toggle the setting, with nothing on
        screen suggesting that prose is clickable. Every other `Field` in the
        app wraps an Input or a Select, where the same quirk only means "focus
        the input"; this is the first checkbox to go in one, and it is the
        control where a stray click costs the most.
      */}
      {/*
        Above the approval block and mutually exclusive with it: a task with
        no callback token has nothing for `auto_merge` to apply to — there is
        no report for it to waive the approval on — so checking this clears
        that one (and the server would refuse both anyway — see the
        20260917130000 migration).
      */}
      <div className="flex flex-col gap-1.5">
        <span className="text-[11.5px] font-semibold text-mute">Tracking</span>
        <Checkbox
          id="intake-fire-and-forget"
          checked={fireAndForget}
          onChange={(checked) => {
            setFireAndForget(checked);
            if (checked) setAutoMerge(false);
          }}
          label="No callback — fire and forget"
        />
        <span className="text-[11.5px] leading-relaxed text-faint">
          The prompt goes straight to a routine with no id, no URL, nothing to approve, nothing
          to poll or send back. Rigg still keeps a task for the record, but it stays off the
          board — filter by source to find it. Use this for work that is not worth tracking — a
          ping, a one-off nudge.
        </span>
      </div>
      {fireAndForget ? null : (
        <div className="flex flex-col gap-1.5">
          <span className="text-[11.5px] font-semibold text-mute">Approval</span>
          <Checkbox
            id="intake-auto-merge"
            checked={autoMerge}
            onChange={setAutoMerge}
            label="Work from this intake is approved from the outset"
          />
          <span className="text-[11.5px] leading-relaxed text-faint">
            Everything else still gates: Rigg only lands work that is clean, green, and whose
            reviewed_sha matches the head. This waives the human click, not the review — and
            sending the work back withdraws it.
          </span>
        </div>
      )}
    </Dialog>
  );
}

/**
 * The URL, readable as often as you like.
 *
 * Every other secret in Rigg is shown once. This one is not, because it lives in
 * somebody's cron config and gets looked up months later — and a URL you cannot
 * recover is one people route around by making a second intake and abandoning
 * the first, which is strictly worse for everyone.
 */
function IntakeUrlDialog({ intakeId, onClose }: { intakeId: string; onClose: () => void }) {
  const { data } = useRigg();
  const { notify } = useToast();
  const intake = data.intakes[intakeId];

  const [state, setState] = useState<{
    url: string;
    creation: string;
    registration: string;
  } | null>(null);
  const [tab, setTab] = useState<'creation' | 'registration'>('creation');
  const [error, setError] = useState<string | null>(null);

  useMemo(() => {
    void api
      .intakeUrl(intakeId)
      .then(setState)
      .catch((e: Error) => setError(e.message));
  }, [intakeId]);

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify(`${what} copied`);
    } catch {
      notify('Could not copy');
    }
  };

  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => !open && onClose()}
      title={intake ? `${intake.name} — how to use it` : 'Intake'}
      description={
        intake?.fire_and_forget
          ? 'Anyone holding this URL can send work straight to a routine in this project — no callback, no task on the board. Treat it as a password.'
          : 'Anyone holding this URL can open tasks in this project. Treat it as a password.'
      }
      footer={<Button onClick={onClose}>Done</Button>}
    >
      {error ? (
        <p className="text-[13px] text-warnx">{error}</p>
      ) : !state ? (
        <p className="text-[13px] text-mute">Reading…</p>
      ) : (
        <>
          <Field label="URL">
            <div className="flex gap-2">
              <Input readOnly value={state.url} className="font-mono sm:text-[12px]" onFocus={(e) => e.currentTarget.select()} />
              <Button onClick={() => void copy(state.url, 'URL')}>Copy</Button>
            </div>
          </Field>

          <SectionLabel className="mt-5 mb-2">Instructions</SectionLabel>
          <div className="mb-2.5 flex flex-wrap gap-2">
            <Tip content="Hand work over and walk away. Nothing to report back.">
              <Button
                size="sm"
                tone={tab === 'creation' ? 'primary' : 'ghost'}
                onClick={() => setTab('creation')}
              >
                Creating
              </Button>
            </Tip>
            {intake?.fire_and_forget ? null : (
              <Tip content="For a routine that is doing the work itself and will report on it.">
                <Button
                  size="sm"
                  tone={tab === 'registration' ? 'primary' : 'ghost'}
                  onClick={() => setTab('registration')}
                >
                  Registering
                </Button>
              </Tip>
            )}
            <span className="flex-1" />
            <Button
              size="sm"
              onClick={() =>
                void copy(tab === 'creation' ? state.creation : state.registration, 'Instructions')
              }
            >
              Copy
            </Button>
          </div>
          <pre className="max-h-[46vh] overflow-auto rounded-xl border border-line bg-panel p-3.5 font-mono text-[12px] leading-relaxed whitespace-pre-wrap text-t6">
            {tab === 'creation' ? state.creation : state.registration}
          </pre>
        </>
      )}
    </Dialog>
  );
}
